import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { GTC_HEADER_HEIGHT } from "./constants";

export const FAB_SIZE = 60;
const FAB_MARGIN = 24;
const EDGE_GUTTER = 8;
const DRAG_THRESHOLD_PX = 4;

const STORAGE_KEY = "nexa:launcher-position";

export interface FabPosition {
  x: number;
  y: number;
}

interface Viewport {
  width: number;
  height: number;
}

const readViewport = (): Viewport =>
  typeof window === "undefined" ? { width: 0, height: 0 } : { width: window.innerWidth, height: window.innerHeight };

const clampToViewport = ({ x, y }: FabPosition, minX: number, viewport: Viewport): FabPosition => {
  const maxX = Math.max(minX, viewport.width - FAB_SIZE - EDGE_GUTTER);
  const minY = GTC_HEADER_HEIGHT + EDGE_GUTTER;
  const maxY = Math.max(minY, viewport.height - FAB_SIZE - EDGE_GUTTER);
  return {
    x: Math.min(Math.max(x, minX), maxX),
    y: Math.min(Math.max(y, minY), maxY),
  };
};

const defaultPosition = (): FabPosition => {
  const { width, height } = readViewport();
  return { x: width - FAB_SIZE - FAB_MARGIN, y: height - FAB_SIZE - FAB_MARGIN };
};

const readStored = (): FabPosition | null => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<FabPosition>;
    if (typeof parsed?.x !== "number" || typeof parsed?.y !== "number") return null;
    return { x: parsed.x, y: parsed.y };
  } catch {
    return null;
  }
};

export const useFabPosition = ({ minX = EDGE_GUTTER }: { minX?: number } = {}) => {
  const [anchor, setAnchor] = useState<FabPosition>(() => (typeof window === "undefined" ? { x: 0, y: 0 } : readStored() ?? defaultPosition()));
  const [viewport, setViewport] = useState<Viewport>(readViewport);
  const [dragging, setDragging] = useState(false);

  const dragRef = useRef<{ offsetX: number; offsetY: number; moved: boolean } | null>(null);

  const persist = useCallback((next: FabPosition) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
    }
  }, []);

  useEffect(() => {
    const onResize = () => setViewport(readViewport());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const position = useMemo(() => clampToViewport(anchor, minX, viewport), [anchor, minX, viewport]);

  const onPointerDown = useCallback((e: React.PointerEvent<HTMLElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    dragRef.current = { offsetX: e.clientX - rect.left, offsetY: e.clientY - rect.top, moved: false };
    e.currentTarget.setPointerCapture(e.pointerId);
    setDragging(true);
  }, []);

  const onPointerMove = useCallback(
    (e: React.PointerEvent<HTMLElement>) => {
      const drag = dragRef.current;
      if (!drag) return;
      const next = clampToViewport({ x: e.clientX - drag.offsetX, y: e.clientY - drag.offsetY }, minX, viewport);
      if (!drag.moved) {
        const rect = e.currentTarget.getBoundingClientRect();
        if (Math.abs(next.x - rect.left) + Math.abs(next.y - rect.top) > DRAG_THRESHOLD_PX) drag.moved = true;
      }
      if (drag.moved) setAnchor(next);
    },
    [minX, viewport]
  );

  const onPointerUp = useCallback(
    (e: React.PointerEvent<HTMLElement>): boolean => {
      const drag = dragRef.current;
      dragRef.current = null;
      setDragging(false);
      if (e.currentTarget.hasPointerCapture?.(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
      if (!drag) return false;
      if (drag.moved) {
        setAnchor((current) => {
          persist(current);
          return current;
        });
        return false;
      }
      return true;
    },
    [persist]
  );

  return { position, dragging, onPointerDown, onPointerMove, onPointerUp };
};
